/**
 * Stage 2.2 — "something the client counts has changed".
 *
 * `ClientWorkContext` holds the ONE `getMyClientWork` read behind the shell's
 * badges and the dashboard's "Ootab sind". Instead of every approval dialog,
 * change-order card or message composer remembering to call `refresh()`,
 * the callable wrapper (`httpsCallableWithLog`) announces each successful
 * call of a callable listed here, and the context re-reads. A new screen that
 * approves hours gets a fresh badge for free.
 *
 * The list is the set of callables that can add or remove an "Ootab sind"
 * line or a "RM24 tegeleb" line (see functions/src/clients/lib/clientAttention.ts).
 * A listed call outside the client shell is harmless: nobody listens.
 */

export const CLIENT_WORK_CHANGED_EVENT = 'rm24:client-work-changed';

export const CLIENT_WORK_MUTATIONS: ReadonlySet<string> = new Set([
  // approve_hours / disputed_hours
  'approveTimesheet',
  'rejectTimesheet',
  'finalizeTimesheetRejection',
  'decideTimeCorrection',
  // decide_change_order
  'approveJobAdjustment',
  'rejectJobAdjustment',
  'requestJobAdjustment',
  // reply_message
  'postMessage',
  'postOrderMessage',
  'postSiteMessage',
  // orders: order_review, staffing_gap, pay_prepayment, prepayment_hold
  'createJobOrder',
  'cancelOrder',
  'cancelJob',
  'changeStaffing',
  'changeJobEndDate',
  'updateJobRequirements',
  'requestSubstitution',
  'endJobWork',
  'resolveEndingSoon',
  // UX 5.6 — answer_date_proposal (and RM24's proposal adds one)
  'respondDateProposal',
  'proposeOrderDates',
  // object responsibility and delegated rights decide whose „Ootab sind” a
  // line is (approve_hours / reply_message follow the rights)
  'handOverSiteResponsibility',
  'endAccessDelegation',
  'extendAccessDelegation',
]);

/** Called by the callable wrapper after a call returned without error. */
export function announceCallableSuccess(name: string): void {
  if (!CLIENT_WORK_MUTATIONS.has(name)) return;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CLIENT_WORK_CHANGED_EVENT, { detail: { callable: name } }));
}

/** Subscribe; returns the unsubscribe. */
export function onClientWorkChanged(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(CLIENT_WORK_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CLIENT_WORK_CHANGED_EVENT, listener);
}
